// Prontidão do host do modal cliente-360, por MÓDULO (moduleKey — ex.: 'crm',
// 'compliance'). Generalização de cliente-crm-modal-host.js: cada módulo tem
// sua própria contagem/waiters, isolados entre si (marcar 'crm' como montado
// não afeta a prontidão de 'compliance', e vice-versa).
//
// Quem realmente monta e desmonta o modal de cada módulo é SEMPRE a mesma
// instância de componente (ex.: crm-lead-steps.vue, compliance-client-overview.vue),
// não importa onde ela é montada (no CRM, há dois lugares possíveis — shell ou
// crm-nav-bar — nunca os dois ao mesmo tempo; ver navegacao-v2-shell.vue).
//
// Prontidão por módulo é um CONTADOR de instâncias montadas (não um boolean "já
// carregou uma vez"): o componente do modal chama markMounted(moduleKey)/
// markUnmounted(moduleKey) no próprio created()/beforeDestroy(). Um contador
// (em vez de um boolean sobrescrito por cada instância) evita "ready" cair pra
// false por engano se o Vue montar a nova instância antes de destruir a antiga
// na mesma flush de navegação.
//
// waitReady(moduleKey) existe porque o chunk da VIEW de destino (ex.:
// crm-nav-bar.vue, carregado via lazyLoadView — ver @utils/LazyLoad.js) é um
// async component ANINHADO com loading/delay/error próprios, resolvido de
// forma independente do router.push() e de $nextTick(): nenhum dos dois
// garante que o host já esteja montado depois de navegar. Quem precisa abrir
// o modal DEPOIS de navegar (ex.: a sidebar, ao entrar num módulo pela
// navegação client-centric) deve `await waitReady(moduleKey)` antes de emitir
// o evento do modal — senão o evento sai pro vazio (nenhum host ainda montado)
// ou pega o host antigo no instante em que está sendo destruído (dialog abre e
// é destruído no mesmo tick, só o scrim/overlay fica órfão).
const activeCounts = new Map()
const waitersByModule = new Map()

function resolveWaiters(moduleKey) {
  const pending = waitersByModule.get(moduleKey) || []
  waitersByModule.set(moduleKey, [])
  pending.forEach((resolve) => resolve(true))
}

/**
 * @param {string} moduleKey
 * @returns {boolean}
 */
export function isClienteModalHostReady(moduleKey) {
  return (activeCounts.get(moduleKey) || 0) > 0
}

export function markClienteModalHostMounted(moduleKey) {
  const next = (activeCounts.get(moduleKey) || 0) + 1
  activeCounts.set(moduleKey, next)
  if (next === 1) resolveWaiters(moduleKey)
}

export function markClienteModalHostUnmounted(moduleKey) {
  const next = Math.max(0, (activeCounts.get(moduleKey) || 0) - 1)
  activeCounts.set(moduleKey, next)
}

/**
 * @param {string} moduleKey
 * @param {Object} opts
 * @param {number} opts.timeoutMs tempo máximo de espera (circuit breaker —
 *   evita travar pra sempre se o chunk falhar em carregar por qualquer motivo).
 * @returns {Promise<boolean>} true se o host ficou pronto; false se estourou o timeout.
 */
export function waitClienteModalHostReady(moduleKey, { timeoutMs = 6000 } = {}) {
  if (isClienteModalHostReady(moduleKey)) return Promise.resolve(true)
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), timeoutMs)
    const waiters = waitersByModule.get(moduleKey) || []
    waiters.push((ready) => {
      clearTimeout(timer)
      resolve(ready)
    })
    waitersByModule.set(moduleKey, waiters)
  })
}

// Uso exclusivo em testes — evita vazamento de contador/waiters entre suítes.
export function _resetClienteModalHost() {
  activeCounts.clear()
  waitersByModule.clear()
}
